import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    selectIsNative,
    selectPreferences,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Button from 'yoda-core-components/lib/components/Button';
import { appSignIn } from 'yoda-site-components/lib/actions/NativeAppAction';
import { openRewardsPromotionLogin } from 'yoda-site-components/lib/actions/SignInSliderActions';
import FSHRedTruck from 'yoda-site-components/lib/assets/svg/fsh-red-truck.svg';
import { triggerModulePageTracking } from 'yoda-site-components/lib/actions/AnalyticsAction';
import { RELOAD_LOCATION } from 'yoda-site-components/lib/actionTypes/SigninActionTypes';
import { enableFeatureInApp } from 'yoda-site-components/lib/helpers/Utils/features';
import { getPLCCDetailsHtml, setPLCCSliderOpen } from 'yoda-site-components/lib/actions/PLCCAction';
import {
    selectRewardsTier,
    selectUserState,
} from 'yoda-site-components/lib/selectors/UserProfileSelector';
import {
    selectCreditCustomerThreshold,
    selectRewardCustomerThreshold,
} from 'yoda-site-components/lib/selectors/FreeShippingThresholdSelector';

function FreeShippingThreshold(props) {
    const { renderFrom, closeInterstitial, wrapperStyles } = props;
    /** if cartSignIn render Signin Text instead of buttion
     *  if AtcSlider close slider and open signinslider
     */
    const fromCartSignIn = renderFrom === 'cartSignIn';
    const fromATCSlider = renderFrom === 'ATCSlider';
    const fromCartOrderSummary = renderFrom === 'cartOrderSummary';
    const fromPDP = renderFrom === 'PDP';
    const dispatch = useDispatch();
    const isNative = useSelector((state) => selectIsNative(state));
    const isAndroid = useSelector((state) => state.context?.isNativeAndroid ?? false);
    const irisRewardCustomerThreshold = useSelector(selectRewardCustomerThreshold);
    const irisCreditCustomerThreshold = useSelector(selectCreditCustomerThreshold);
    const rewardsTier = useSelector(selectRewardsTier);
    const userState = useSelector(selectUserState);
    const isCardHolder = rewardsTier.toLowerCase?.() === 'credit';
    const isUserSignedIn = userState === '1';
    const showSignInFSH = irisRewardCustomerThreshold && (!isUserSignedIn || fromCartSignIn);
    const preferences = useSelector((state) => selectPreferences(state));
    const rewardCustomerThreshold = irisRewardCustomerThreshold;
    const isRewardThresholdZero = rewardCustomerThreshold === '0';
    const isCreditThresholdZero = irisCreditCustomerThreshold === '0';
    const postSignInRefreshMinVersion =
        preferences?.FSHPostSignInRefreshMinVersion?.androidMinVersion || '';
    const cartSignInText = isRewardThresholdZero
        ? 'Sign in for FREE Shipping'
        : `Sign in for FREE Shipping on $${rewardCustomerThreshold}+`;
    const fromOtherSourceText = isRewardThresholdZero
        ? ` for FREE Shipping`
        : ` for FREE Shipping on $${rewardCustomerThreshold}+`;
    const creditText1 = isCreditThresholdZero
        ? 'Get Free Shipping'
        : `Get Free Shipping on $${irisCreditCustomerThreshold}+`;
    const creditText2 = ' when you use a JCPenney Credit Card.';
    const creditText3 = ' Applied at checkout.';
    const plccDynamicBanner = useSelector((state) => state.plccDynamicBanner || {});
    const { plccSliderOpen = false } = plccDynamicBanner;
    /* istanbul ignore next */
    const fetchPLCCDetailsContent = () => {
        const getOfferDetailsContentURL =
            '/content-aggregator/shared-components/zones/S1/s/cf-plcc-slide-panel?searchTerm=cf-plcc-slide-panel';
        if (
            !plccSliderOpen &&
            (!plccDynamicBanner?.detailsHtml || plccDynamicBanner?.detailsHtml.length === 0)
        ) {
            dispatch(getPLCCDetailsHtml(getOfferDetailsContentURL));
        }
    };
    const togglePLCCDetailSlider = () => {
        if (fromATCSlider) closeInterstitial();
        fetchPLCCDetailsContent();
        dispatch(setPLCCSliderOpen(!plccSliderOpen));
    };

    const handleSignIn = () => {
        if (fromATCSlider) closeInterstitial();
        dispatch(
            triggerModulePageTracking({
                name: `${fromATCSlider ? 'fsh-atc' : 'fsh'} : signin`,
                linktext: 'sign in',
            })
        );
        if (isNative) {
            const enableRefreshforAndroid = enableFeatureInApp({
                iosMinVersion: '',
                androidMinVersion: postSignInRefreshMinVersion,
            });
            /** Refresh from web for Android happens twice, ios has auto refresh by default */
            const refreshfromWeborApp = enableRefreshforAndroid
                ? 'WEB_REFRESH_SIGN_IN_REQUEST'
                : RELOAD_LOCATION;
            const returnActionAppSignin =
                (fromPDP || fromCartOrderSummary || fromATCSlider) && isAndroid
                    ? refreshfromWeborApp
                    : '';
            dispatch(appSignIn(returnActionAppSignin));
        } else dispatch(openRewardsPromotionLogin());
    };
    const wrapperClass = dt([fromCartSignIn ? 'mb-4' : '']);

    if (!showSignInFSH && (!irisCreditCustomerThreshold || fromCartSignIn)) return null;
    return showSignInFSH ? (
        <div className={`${wrapperClass} ${wrapperStyles}`}>
            <Icon
                iconType="svg"
                automationId="fshRedTruckIcon"
                className={
                    fromCartSignIn ? dt(['ml-2', 'mr-2']) : dt([fromPDP ? 'px-[3px]' : 'px-1'])
                }
                width={fromCartSignIn ? '40' : '32'}
                height={fromCartSignIn ? '40' : '32'}
                viewBox={fromCartSignIn ? '0 0 50 50' : '0 0 32 32'}
            >
                <FSHRedTruck />
            </Icon>
            <div className={dt([fromPDP ? 'ml-1.5' : 'inline'])}>
                {!fromCartSignIn && (
                    <Button
                        buttonType="Link"
                        size="Sm"
                        className={dt([fromPDP ? '!inline' : 'ml-1.5'])}
                        automationId="fsh-signin"
                        onClick={handleSignIn}
                    >
                        Sign in{` `}
                    </Button>
                )}
                <div
                    className={
                        fromCartSignIn
                            ? dt(['text-medium', 'mr-2', 'ml-1.5', 'inline-block'])
                            : dt(['text-small', 'inline-block', fromPDP ? 'inline' : 'ml-1'])
                    }
                >
                    {fromCartSignIn ? cartSignInText : fromOtherSourceText}
                </div>
            </div>
        </div>
    ) : (
        <div
            className={`${dt([
                'relative',
                'mt-1',
                fromATCSlider && 'md:mb-1.5',
                fromATCSlider && 'md:mt-2.5',
            ])} ${wrapperStyles}`}
        >
            <Icon
                iconType="svg"
                automationId="fshRedTruckIcon"
                className={dt(['px-[3px]', 'absolute', '-top-[3px]'])}
                width="32"
                height="32"
                viewBox="0 0 32 32"
            >
                <FSHRedTruck />
            </Icon>
            <div className={dt(['ml-[38px]', 'text-small', fromCartOrderSummary && 'lg:pr-8'])}>
                <span className={dt(['font-bold'])}>{creditText1}</span>
                {creditText2}
                {fromCartOrderSummary && creditText3}
                {!isCardHolder && (
                    <span onMouseEnter={fetchPLCCDetailsContent}>
                        {' '}
                        <Button
                            buttonType="Link"
                            size="Sm"
                            automationId="fsh-credit-card-learn-how"
                            onClick={togglePLCCDetailSlider}
                        >
                            Learn&nbsp;More
                        </Button>
                    </span>
                )}
            </div>
        </div>
    );
}

FreeShippingThreshold.propTypes = {
    renderFrom: PropTypes.string,
    closeInterstitial: PropTypes.func,
    wrapperStyles: PropTypes.string,
};
FreeShippingThreshold.defaultProps = {
    renderFrom: '',
    closeInterstitial: () => {},
    wrapperStyles: '',
};

export default FreeShippingThreshold;
